import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Select, SelectOption, Text, Title } from "rizzui";
import {
  EEnquiryEntryContactStatus,
  IEnquiryEntry,
} from "@/utilities/types/EnquiryEntry";
import dayjs from "dayjs";
import { PiLinkBold } from "react-icons/pi";
import { BiSolidDislike, BiSolidLike } from "react-icons/bi";
import LeadAcquisitionAndScore from "./LeadAcquisitionAndScore";
import { Z_MODAL } from "@/config/zModal";
import { IoMdClose } from "react-icons/io";
import EnquiryEntriesRepository from "@/utilities/repositories/EnquiryEntries";
import useApi from "@/hooks/useApi";
import { leadScore } from "@/utilities/functions/lead";
import ScoreDetails from "@/components/score-details";
import { ENQUIRY_ENTRY_CONTACT_STATUS } from "@/config/constants";
import OptionItemLayout from "../../homepage-layout/forms/OptionItemLayout";
import convertToSingaporeTime from "@/utilities/functions/convertToSingaporeTime";
import { useNavigate } from "react-router-dom";
import { routes } from "@/config/routes";

interface EnquiryEntryDataFormProps {
  data?: IEnquiryEntry;
  onRefresh: () => void;
  isReadOnly?: boolean;
  token?: string;
}

export default function EnquiryEntryDataForm({
  data,
  onRefresh,
  isReadOnly = false,
  token = "",
}: EnquiryEntryDataFormProps) {
  const navigate = useNavigate();
  const [modalState, setModalState] = useState(false);
  const [modalStateScoreDetails, setModalStateScoreDetails] = useState(false);
  const [contactStatus, setContactStatus] =
    useState<EEnquiryEntryContactStatus>(
      (data as IEnquiryEntry)?.contactStatus || EEnquiryEntryContactStatus.NEW,
    );
  const { request: getLogsByEnquiryId, response: enquiryLogsResponseById } =
    useApi({
      request: EnquiryEntriesRepository.getLogsById,
    });
  const { request: getLogsByToken, response: enquiryLogsResponseByToken } =
    useApi({
      request: () => EnquiryEntriesRepository.getLogsByToken(token),
    });
  const { request: updateEnquiryEntry, loading } = useApi({
    request: EnquiryEntriesRepository.update,
    enableToast: true,
  });
  const { parsedInterestedIn, parsedUnitTypes } = useMemo(() => {
    try {
      const parsedInterestedIn: string[] = JSON.parse(data?.interestedIn || "");
      const parsedUnitTypes: string[] = JSON.parse(data?.unitTypes || "");
      return { parsedInterestedIn, parsedUnitTypes };
    } catch (e) {
      return { parsedInterestedIn: [], parsedUnitTypes: [] };
    }
  }, [data?.interestedIn, data?.unitTypes]);

  const formatDisplayValue = (
    value: string | string[] | undefined | null,
  ): string => {
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(", ") : "-";
    }
    if (typeof value === "string") {
      return value.length > 0 ? value : "-";
    }
    return "-";
  };

  const projectName =
    typeof data?.project?.name === "object"
      ? data?.project?.name?.en
      : data?.project?.name || "N/A";

  const projectDistrict =
    typeof data?.project?.location?.name === "object"
      ? data?.project?.location?.name?.en
      : data?.project?.location?.name || "N/A";

  const availableUnits = data?.project?.manualTotalUnitsCount || 0;

  const handleFeedback = useCallback(() => {
    navigate(routes.auth.signIn);
  }, []);

  useEffect(() => {
    if (isReadOnly && !!token) {
      getLogsByToken(token);
      return;
    } else if (data?.id && typeof getLogsByEnquiryId === "function") {
      getLogsByEnquiryId(data.id);
    }
  }, [data?.id, isReadOnly, token]);

  useEffect(() => {
    setContactStatus(data?.contactStatus || EEnquiryEntryContactStatus.NEW);
  }, [data?.contactStatus]);

  const enquiryLogsResponse =
    isReadOnly && !!token
      ? enquiryLogsResponseByToken
      : enquiryLogsResponseById;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 p-3">
      {/* Left Column - Interested In */}
      <div className="space-y-3 md:space-y-6">
        <div>
          <div className="mb-2 flex gap-3 justify-between">
            <Title as="h3" className="text-xl font-semibold text-gray-900">
              Interested In
            </Title>

            {isReadOnly && (
              <div
                className="flex flex-shrink-0 flex-row items-center justify-center overflow-hidden rounded-full min-w-fit border border-gray-200"
                style={{ height: "54px" }}
              >
                <Button
                  variant="text"
                  size="lg"
                  className="flex h-full flex-col items-center justify-center !text-green-600"
                  onClick={() => handleFeedback()}
                >
                  <BiSolidLike size={20} />
                  <Text className="text-xs font-semibold">Success</Text>
                </Button>
                <Button
                  size="lg"
                  variant="text"
                  className="flex h-full flex-col items-center justify-center pl-0 !text-red-600"
                  onClick={() => handleFeedback()}
                >
                  <BiSolidDislike size={20} />
                  <Text className="text-xs font-semibold">Failed</Text>
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-1">
            <div
              className="flex items-center gap-2 cursor-pointer underline underline-offset-1"
              onClick={() => {
                if (!data?.submittedFromDomain) return;
                if (!data.submittedFromDomain.startsWith("https://")) {
                  window.open(`https://${data.submittedFromDomain}`, "_blank");
                  return;
                }
                window.open(data.submittedFromDomain, "_blank");
              }}
            >
              <span className="text-lg font-medium text-gray-900">
                {projectName}
              </span>
              <PiLinkBold className="w-4 h-4 text-gray-400" />
            </div>

            <div className="text-sm text-gray-500">{projectDistrict}</div>

            <div className="text-sm text-gray-500">
              {availableUnits > 0 ? `${availableUnits} Units Available` : ""}
            </div>
          </div>
        </div>

        <div>
          <Title as="h4" className="text-lg font-medium text-gray-900">
            Preferred Appointment Date & Time
          </Title>
          <div className="text-gray-500">
            {data?.appointment && dayjs(data.appointment).isValid()
              ? `${convertToSingaporeTime(
                  new Date(data.appointment),
                  "MMMM DD, YYYY hh:mm A",
                )} (SG Time)`
              : "-"}
          </div>
        </div>

        <div>
          <Title as="h4" className="text-lg font-medium text-gray-900">
            Unit Types Selected
          </Title>
          <div className="text-gray-500">
            {formatDisplayValue(parsedUnitTypes)}
          </div>
        </div>

        <div>
          <Title as="h4" className="text-lg font-medium text-gray-900">
            Interested In
          </Title>
          <div className="text-gray-500">
            {formatDisplayValue(parsedInterestedIn)}
          </div>
        </div>

        {data?.customField && (
          <div>
            <Title as="h4" className="text-lg font-medium text-gray-900">
              Custom Field
            </Title>
            <div className="text-gray-500">{data.customField}</div>
          </div>
        )}
      </div>

      {/* Right Column - Enquiry Details */}
      <div className="space-y-2 md:space-y-3">
        <div>
          <Title
            as="h3"
            className="text-xl font-semibold text-gray-900 mb-2 md:mb-4"
          >
            Enquiry Details
          </Title>

          <div className="space-y-2">
            <div className="text-gray-900">
              {data?.createdAt
                ? convertToSingaporeTime(new Date(data.createdAt), "DD MMM")
                : ""}{" "}
              •{" "}
              {data?.createdAt
                ? convertToSingaporeTime(new Date(data.createdAt), "hh:mm A")
                : ""}{" "}
              • {projectName}
            </div>

            <div>
              <Button
                variant="text"
                className="text-sm text-gray-500 underline cursor-pointer !p-0 h-fit"
                onClick={() => setModalStateScoreDetails(true)}
              >
                How to view details?
              </Button>
            </div>
          </div>
        </div>

        <Button
          variant="text"
          className="bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-3xl !mt-4"
          onClick={() => setModalState(true)}
        >
          View History
        </Button>

        <div className="col-span-full">
          <Select
            options={ENQUIRY_ENTRY_CONTACT_STATUS}
            onChange={async (value: EEnquiryEntryContactStatus) => {
              updateEnquiryEntry(data?.id || "", {
                contactStatus: value,
              }).then(async () => {
                setContactStatus(value);
                onRefresh();
              });
            }}
            label={
              <Title as="h4" className="text-lg font-medium text-gray-900">
                Lead Status
              </Title>
            }
            value={contactStatus}
            className="mb-3"
            getOptionValue={(option: SelectOption) => option.value}
            displayValue={(selected: string) => {
              const findOption: { label: string; value: string } | undefined =
                ENQUIRY_ENTRY_CONTACT_STATUS?.find(
                  (option) => option.value === selected,
                );

              return findOption?.label;
            }}
            getOptionDisplayValue={(option) => {
              return <OptionItemLayout option={option} />;
            }}
            dropdownClassName={"z-modal [&_li>div]:w-full"}
            disabled={loading || isReadOnly}
          />
        </div>
      </div>
      <Modal
        isOpen={modalState}
        onClose={() => setModalState(false)}
        className={Z_MODAL.ENQUIRY_DETAILS}
        containerClassName="!max-w-6xl md:!max-w-fit !shadow-2xl"
      >
        <div className="p-6 bg-white rounded-lg">
          <div className="flex justify-end items-start w-full">
            <Button
              variant="text"
              onClick={() => setModalState(false)}
              className="p-0 -translate-y-3"
            >
              <IoMdClose size={24} />
            </Button>
          </div>
          <LeadAcquisitionAndScore data={data} />
        </div>
      </Modal>

      <Modal
        isOpen={modalStateScoreDetails}
        onClose={() => setModalStateScoreDetails(false)}
        className={Z_MODAL.ENQUIRY_DETAILS}
        customSize="600px"
      >
        <div className="p-6 flex flex-col gap-2">
          <div className="flex justify-between items-center w-full">
            <label className="text-lg font-bold">
              Lead Quality Score Detail
            </label>
            <Button
              variant="text"
              className="p-0 -translate-y-2"
              onClick={() => setModalStateScoreDetails(false)}
            >
              <IoMdClose size={24} />
            </Button>
          </div>

          <div
            className="rounded-lg border min-h-[254px]"
            color={
              leadScore(enquiryLogsResponse?.data?.score || 0)?.rizzuiColor
            }
            style={{
              borderColor: leadScore(enquiryLogsResponse?.data?.score || 0)
                ?.color,
            }}
          >
            <ScoreDetails enquiryLogsResponse={enquiryLogsResponse!} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
